import { SocketStateModel, useWebSocketApi } from '@e3dc-react/shell/Hooks/use-websocket-api'
import { useMultiWebSocketApi } from '@e3dc-react/shell/Hooks'
import { StorageStatusDto } from '../api/dto/storage.dto'
import { StorageStatusModel } from '../models/storage.models'
import { fromStorageStatusDtoToModelTransformer as storageDtoToModelTransformer } from 'src/Libs/storage/transformers/from-storage-dto-to-model.transformer'
import { isUndefined } from 'lodash'
import { useFarmContext } from 'src/Elements/Farm'
import { useMemo } from 'react'
import { PowermeterLiveDataModel } from 'src/Libs/power-meters'
import { isFlowTheme } from 'src/Libs/utils'

const SERVICE_ID = '[STORAGE LIVEDATA]'

/**
 * This hook provides status data of a storage through a websocket connection
 * Some storages are only batteries and therefore handled with the same route as storages.
 */
const useLiveStorageStatus = (storageId?: string, doNotInitialize?: boolean): SocketStateModel<StorageStatusModel> => {
  const { isFarmMasterSelected, farmClients } = useFarmContext()

  const storageWebSocketApiPath = `storages/${storageId}/status/ws`
  const { socketState } = useWebSocketApi<StorageStatusModel, StorageStatusDto>({
    apiResourcePath: storageWebSocketApiPath,
    loggerKey: SERVICE_ID,
    transformer: storageDtoToModelTransformer,
    dependencies: [storageId],
    doNotInitialize: isUndefined(storageId) || doNotInitialize,
    endpoint: process.env.REACT_APP_API_ENDPOINT ?? '',
  })

  const storageWebSocketApiPaths = useMemo(() => {
    const clientList = farmClients?.map(client => client.storageId) || []

    return clientList.map(storageId => `storages/${storageId}/status/ws`)
  }, [farmClients])

  const { multiSocketState } = useMultiWebSocketApi<StorageStatusModel, StorageStatusDto>({
    apiResourcePaths: storageWebSocketApiPaths,
    loggerKey: SERVICE_ID,
    transformer: storageDtoToModelTransformer,
    dependencies: [storageId],
    doNotInitialize: isUndefined(storageId) || doNotInitialize || !isFarmMasterSelected || isFlowTheme,
    endpoint: process.env.REACT_APP_API_ENDPOINT ?? '',
  })

  const socketStateForFarmServer = useMemo(() => {
    if (!socketState.data) return socketState

    const powermeters = Object.values(multiSocketState.data ?? {})
      .map(data => data.powermeters)
      .filter((pm): pm is PowermeterLiveDataModel[] => Array.isArray(pm))
      .flat()

    return {
      ...socketState,
      data: {
        ...socketState.data,
        powermeters,
      },
    }
  }, [socketState, multiSocketState.data])

  // Flow has no farm clients, the master's own socket already carries its powermeters
  return isFarmMasterSelected && !isFlowTheme ? socketStateForFarmServer : socketState
}

export default useLiveStorageStatus
